import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi, refreshAccessToken } from '../services/authApi';
import { User, AuthContextType, UserStatus } from '../types/auth';
import { decodeJWT, isTokenExpired } from '../utils/jwt';
import { isPublicPath } from '../utils/publicPaths';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const clearAuth = () => {
    localStorage.removeItem('auth_token');
    setToken(null);
    setUser(null);
  };

  const redirectToLogin = () => {
    if (!isPublicPath()) {
      window.location.href = '/login';
    }
  };

  const applyToken = (activeToken: string) => {
    const payload = decodeJWT(activeToken);
    if (!payload?.user_info) {
      clearAuth();
      return false;
    }
    const u = payload.user_info;
    setToken(activeToken);
    setUser({
      id: u.id,
      email: u.email,
      first_name: u.first_name,
      last_name: u.last_name,
      role_id: u.role_id,
      organization_id: u.organization_id,
      status: UserStatus.ACTIVE,
      email_verified: true,
    });
    return true;
  };

  // On mount: validate stored token. If it has expired locally, try the
  // sliding refresh first (the session cookie may still be valid if the user
  // returned within the session window) before logging out — so "leave and
  // come back" doesn't bounce to login unless the session is truly gone.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // The embedded HIS widget owns its own JWT (arrives via the ?token= URL
      // param and is written to localStorage by WidgetPage). AuthProvider must
      // NOT read / refresh / clear it here — that races with WidgetPage's write
      // and can wipe the fresh token or bounce the iframe to /login. Stay out.
      if (window.location.pathname.startsWith('/widget')) {
        setIsLoading(false);
        return;
      }

      const storedToken = localStorage.getItem('auth_token');
      if (!storedToken) {
        clearAuth();
        setIsLoading(false);
        return;
      }

      let activeToken = storedToken;
      if (isTokenExpired(storedToken)) {
        const refreshed = await refreshAccessToken();
        if (cancelled) return;
        if (!refreshed) {
          clearAuth();
          setIsLoading(false);
          redirectToLogin();
          return;
        }
        activeToken = refreshed;
      }

      applyToken(activeToken);
      setIsLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await authApi.login(email, password);
      setToken(response.access_token);
      const currentUser = authApi.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = () => {
    authApi.logout();
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    const currentUser = authApi.getCurrentUser();
    if (currentUser) setUser(currentUser);
  };

  return (
    <AuthContext.Provider value={{
      user, token,
      isAuthenticated: !!token && !!user,
      isLoading, login, logout, refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
